import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import ApiService from '../../../../services/ApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { initialProfilesMock } from './mockProfilesData'
import { ProfileCard } from './ProfileCard'
import { ProfileData, ModuleGroupData } from './types'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: 24px;
  background-color: #f8fafc;
  overflow-y: auto;
  box-sizing: border-box;
`

const ProfilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
`

const FooterInstruction = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  margin-top: auto;
  padding-top: 16px;
`

const LightbulbIcon = styled.span`
  color: #f59e0b;
  font-size: 14px;
`

const LoadingText = styled.div`
  padding: 20px;
  font-size: 14px;
  color: #64748b;
  text-align: center;
`



const NewProfilesTable: React.FC = () => {
  const [profiles, setProfiles] = useState<ProfileData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [savingId, setSavingId] = useState<number | string | null>(null)
  const { setFeedback } = useContext(FeedbackContext)

  // Módulos que exigem concessão explícita (Whitelist)
  const RESTRICTED_KEYS = new Set([
    'stock', 'materia_prima', 'insumos', 'produto_engenheirado', 'estoque',
    'profile', 'user', 'perfis_acessos', 'regras', 'accesses', 'pessoas',
    'resources', 'resource'
  ])

  // Converter o perfil que vem da API do backend em dados com os checkboxes preenchidos
  const mapBackendProfileToFront = (backendProfile: any): ProfileData => {
    const rawPerms = backendProfile.permissions || []
    const permSet = new Set<string>(rawPerms)
    const hasPermissionsInDb = permSet.size > 0

    // Usamos a estrutura base dos módulos como template e marcamos ver/criar/editar/deletar
    const templateModules = initialProfilesMock[0]?.modules || []

    const isKeyBlocked = (key: string) =>
      permSet.has(`${key}:deny`) || permSet.has(`${key}:block`) || permSet.has(`deny:${key}`) || permSet.has(`block:${key}`)

    const isKeyGranted = (key: string) => {
      if (permSet.has(key) || permSet.has(`${key}:read`)) return true
      if (key === 'vendas' && (permSet.has('orders') || permSet.has('orders:read') || permSet.has('orcamentos'))) return true
      if (key === 'compras' && (permSet.has('purchase') || permSet.has('purchase:read') || permSet.has('demanda'))) return true
      if (key === 'producao' && (permSet.has('production') || permSet.has('production:read') || permSet.has('ordens_producao'))) return true
      if (key === 'financeiro' && (permSet.has('financial') || permSet.has('financial:read') || permSet.has('lancamentos'))) return true
      if (key === 'roteirizacao' && (permSet.has('router') || permSet.has('router:read') || permSet.has('fretes'))) return true
      if (key === 'fiscal' && (permSet.has('fiscal') || permSet.has('fiscal:read') || permSet.has('nota_fiscal'))) return true
      if (key === 'profile' && (permSet.has('accesses') || permSet.has('accesses:read') || permSet.has('user'))) return true
      if (key === 'stock' && (permSet.has('stock') || permSet.has('stock:read') || permSet.has('materia_prima'))) return true
      if ((key === 'insumos' || key === 'resources' || key === 'resource') && (permSet.has('resources') || permSet.has('resources:read') || permSet.has('resource') || permSet.has('resource:read') || permSet.has('insumos') || permSet.has('insumos:read'))) return true
      if (key === 'qualidade' && (permSet.has('certificates') || permSet.has('certificates:read'))) return true
      if (key === 'product-development' && (permSet.has('products') || permSet.has('products:read'))) return true
      if (key === 'companies' && (permSet.has('companies') || permSet.has('companies:read'))) return true
      if (key === 'equipment' && (permSet.has('equipment') || permSet.has('equipment:read'))) return true
      if (key === 'services' && (permSet.has('services') || permSet.has('services:read'))) return true
      if (key === 'analise-credito' && (permSet.has('analise-credito') || permSet.has('analise-credito:read'))) return true
      return false
    }

    const modules: ModuleGroupData[] = templateModules.map((group) => {
      const groupKey = group.id
      const isRestrictedGroup = RESTRICTED_KEYS.has(groupKey)

      const submenus = group.submenus.map((sub) => {
        const key = sub.id
        const isRestrictedSub = RESTRICTED_KEYS.has(key)

        let ver = false
        if (hasPermissionsInDb) {
          ver = isKeyGranted(key)
        } else {
          ver = isRestrictedSub ? isKeyGranted(key) : !isKeyBlocked(key)
        }

        const criar = permSet.has(`${key}:create`)
        const editar = permSet.has(`${key}:update`)
        const deletar = permSet.has(`${key}:delete`)

        return {
          ...sub,
          permissions: {
            ver,
            criar,
            editar,
            deletar,
          },
        }
      })

      let groupVer = false
      if (hasPermissionsInDb) {
        groupVer = isKeyGranted(groupKey)
      } else {
        groupVer = isRestrictedGroup ? isKeyGranted(groupKey) : !isKeyBlocked(groupKey)
      }

      const groupCriar = permSet.has(`${groupKey}:create`)
      const groupEditar = permSet.has(`${groupKey}:update`)
      const groupDeletar = permSet.has(`${groupKey}:delete`)

      return {
        ...group,
        permissions: {
          ver: groupVer,
          criar: groupCriar,
          editar: groupEditar,
          deletar: groupDeletar,
        },
        submenus,
      }
    })

    return {
      id: backendProfile.id,
      name: backendProfile.name,
      userCount: backendProfile._count?.User ?? backendProfile.User?.length ?? 0,
      modules,
    }
  }

  // 1. Buscar perfis dinâmicos da tenant do usuário logado
  const loadProfiles = async () => {
    try {
      setLoading(true)
      const response = await ApiService.profiles.findAll()
      const rawData = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : [])
      if (Array.isArray(rawData)) {
        const formatted = rawData.map(mapBackendProfileToFront)
        setProfiles(formatted)
      }
    } catch (error) {
      console.error('Erro ao carregar perfis do backend:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProfiles()
  }, [])

  // 2. Salvar alterações do perfil no banco (Módulo + Ação + ProfileId)
  const handleSaveProfile = async (updatedProfile: ProfileData) => {
    try {
      setSavingId(updatedProfile.id)

      // Montar os grants com as ações selecionadas (apenas enums válidos do Prisma: READ, CREATE, UPDATE, DELETE)
      const grants: { moduleKey: string; action: 'READ' | 'CREATE' | 'UPDATE' | 'DELETE' }[] = []
      const explicitPermissions: string[] = []

      updatedProfile.modules.forEach((group) => {
        const groupKey = group.id
        const isRestrictedGroup = RESTRICTED_KEYS.has(groupKey)

        if (group.permissions) {
          if (group.permissions.ver) {
            grants.push({ moduleKey: groupKey, action: 'READ' })
            explicitPermissions.push(groupKey, `${groupKey}:read`)
          } else if (!isRestrictedGroup) {
            // Módulo liberado por padrão, mas desmarcado -> registra string de bloqueio em permissions
            explicitPermissions.push(`${groupKey}:deny`)
          }

          if (group.permissions.criar) {
            grants.push({ moduleKey: groupKey, action: 'CREATE' })
            explicitPermissions.push(`${groupKey}:create`)
          }
          if (group.permissions.editar) {
            grants.push({ moduleKey: groupKey, action: 'UPDATE' })
            explicitPermissions.push(`${groupKey}:update`)
          }
          if (group.permissions.deletar) {
            grants.push({ moduleKey: groupKey, action: 'DELETE' })
            explicitPermissions.push(`${groupKey}:delete`)
          }
        }

        group.submenus.forEach((sub) => {
          const moduleKey = sub.id
          const isRestrictedSub = RESTRICTED_KEYS.has(moduleKey)

          if (sub.permissions.ver) {
            grants.push({ moduleKey, action: 'READ' })
            explicitPermissions.push(moduleKey, `${moduleKey}:read`)
          } else if (!isRestrictedSub) {
            // Submenu liberado por padrão, mas desmarcado -> registra string de bloqueio em permissions
            explicitPermissions.push(`${moduleKey}:deny`)
          }

          if (sub.permissions.criar) {
            grants.push({ moduleKey, action: 'CREATE' })
            explicitPermissions.push(`${moduleKey}:create`)
          }
          if (sub.permissions.editar) {
            grants.push({ moduleKey, action: 'UPDATE' })
            explicitPermissions.push(`${moduleKey}:update`)
          }
          if (sub.permissions.deletar) {
            grants.push({ moduleKey, action: 'DELETE' })
            explicitPermissions.push(`${moduleKey}:delete`)
          }
        })
      })

      // Enviar para o backend (PATCH /backend/profile/:id)
      await ApiService.profiles.patch(Number(updatedProfile.id), {
        name: updatedProfile.name,
        permissions: explicitPermissions,
        grants,
      })

      setProfiles((prev) => prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p)))
      setFeedback({
        isOpen: true,
        message: `Permissões do perfil "${updatedProfile.name}" atualizadas com sucesso!`,
        statusOk: true,
      })
    } catch (error) {
      console.error('Erro ao salvar permissões:', error)
      setFeedback({
        isOpen: true,
        message: 'Erro ao atualizar permissões do perfil.',
        statusOk: false,
      })
    } finally {
      setSavingId(null)
    }
  }

  const handleProfileChange = (updatedProfile: ProfileData) => {
    setProfiles((prev) => prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p)))
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingText>Carregando perfis e permissões da empresa...</LoadingText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <ProfilesList>
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            onProfileChange={handleProfileChange}
            onSaveProfile={handleSaveProfile}
            isSaving={savingId === profile.id}
          />
        ))}
      </ProfilesList>

      <FooterInstruction>
        <LightbulbIcon>💡</LightbulbIcon>
        <span>
          Clique no perfil para abrir os módulos e submenus. Marque as ações desejadas e clique em &quot;Salvar Alterações&quot; para persistir as permissões no banco.
        </span>
      </FooterInstruction>
    </PageContainer>
  )
}

export default NewProfilesTable
